/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import { useMessageBar } from '../../../hooks/useMessageBar';
import { tb } from '../../../components/layouts/Table';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { ecoYNcodes, MpItem } from '../../../models/mp/MpItem';
import { Button } from '@mui/material';
import { IconButton } from '../../../components/buttons/IconSVG';
import CustomInputWithSearch from '../../../components/inputs/CustomInputWithSearch';
import { ComboBox } from '../../../components/selects/ComboBox';
import { Code } from '../../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../../apis/admin/CommonCode';

import { useCommonModal } from '../../../hooks/useCommonModal';

interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  title: string;
  isSwitchToEc?: boolean;
  initParam?: {
    lv1OptVal?: string; // 개선목적 분류1 옵션 (mp분류 옵션 1)
  };
  onCallbackData: any;
}

/**
 * 개선목적변경팝업(EC/NC)
 * @param open
 * @param close
 * @param title
 * @param initParam
 * @param isSwitchToEc
 * @param onCallbackData
 * @constructor
 */
export const MpIprvPurpChgPopUp = ({
  open,
  close,
  title,
  isSwitchToEc = false,
  initParam,
  onCallbackData,
}: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [mpIprvPurpChg, setMpIprvPurpChg] = useState<MpItem>();
  const [errors, setErrors] = useState<any>({});
  const [code, setCode] = useState<any>({});

  //init
  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    // MP 분류에 따른 검색조건 코드 조회
    // 2. 개선목적 Level1 (iprvClsfLv1Cd)
    const lv1 = await getCommonCodeNamesCondition({
      cmnGrCd: 'MP_IMPR_TYPE_LVL1',
    });
    const ec3 = await getCommonCodeNamesCondition({
      cmnGrCd: 'MP_IMPR_TYPE_LVL3',
      optValCtn3: 'EC',
    });
    const ec3Codes = new Set(ec3.map((v) => v.optValCtn1));
    const ec1 = lv1.filter((v) => ec3Codes.has(v.cmnCd));

    console.log('filtered1 ', lv1);
    let mpImprTypeLvl1 = lv1;
    if (isSwitchToEc) {
      mpImprTypeLvl1 = ec1;
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl1: mpImprTypeLvl1,
    }));
  };

  const handleConfirm = () => {
    if (!mpIprvPurpChg?.ecoYn) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.개선목적을 선택해주세요.', '개선목적을 선택해주세요.'),
      });
      return;
    }
    if (isSwitchToEc && mpIprvPurpChg?.ecoYn !== 'Y') {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.EC인 개선목적 항목만 선택해주세요.',
          'EC인 개선목적 항목만 선택해주세요'
        ),
      });
      return;
    }

    if (!mpIprvPurpChg?.rmk) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.변경사유를 입력해 주세요.', '변경사유를 입력해 주세요.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: isSwitchToEc
        ? t(
            'com.msg.기존 Item 은 Drop 되고 새로운 Item이 생성 됩니다. 진행하시겠습니까?',
            '기존 Item 은 Drop 되고 새로운 Item이 생성 됩니다. 진행하시겠습니까?'
          )
        : t('com.msg.변경하시겠습니까?', '변경하시겠습니까?'),
      yesCallback: () => {
        onCallbackData(mpIprvPurpChg);
        close();
      },
    });
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.적용', '적용')}
    </Button>,
  ];

  /**
   * 개선목적 Level1 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level2
   */
  const changeSearchConditionByIprvClsfLv1Cd = async (iprvClsfLv1Cd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpIprvPurpChg((prev) => ({
        ...prev,
        iprvClsfLv2Cd: '', // 개선목적 Level2
        iprvClsfLv3Cd: '', // 개선목적 Level3
        ecoYn: '', // eco여부 (ec/nc)
      }));
    }

    let mpImprTypeLvl2 = [] as Code[];
    // 개선목적 Level1에 따른 검색조건 코드 조회
    if (iprvClsfLv1Cd) {
      // 1. 개선목적 Level2 (iprvClsfLv2Cd)
      mpImprTypeLvl2 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL2',
        optValCtn1: iprvClsfLv1Cd,
      });
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl2: mpImprTypeLvl2,
    }));
  };

  /**
   * 개선목적 Level2 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level3
   */
  const changeSearchConditionByIprvClsfLv2Cd = async (iprvClsfLv2Cd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpIprvPurpChg((prev) => ({
        ...prev,
        iprvClsfLv3Cd: '', // 개선목적 Level3
        ecoYn: '', // eco여부 (ec/nc)
      }));
    }

    let mpImprTypeLvl3 = [] as Code[];
    // 개선목적 Level2에 따른 검색조건 코드 조회
    if (iprvClsfLv2Cd) {
      // 1. 개선목적 Level3 (iprvClsfLv3Cd)
      mpImprTypeLvl3 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL3',
        optValCtn2: iprvClsfLv2Cd,
      });

      const cd = (code?.mpImprTypeLvl2 || []).filter((o) => o.cmnCd === iprvClsfLv2Cd);
      if (cd.length && cd[0]?.optValCtn2) {
        const code = ecoYNcodes.filter((o) => o.cmnCdNm === cd[0]?.optValCtn2 || '');
        console.log(code);
        setMpIprvPurpChg((prev) => ({ ...prev, ecoYn: code.length > 0 ? code[0].cmnCd : '' }));
      }
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl3: mpImprTypeLvl3,
    }));
  };

  /**
   * MP유형 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. Item 상태
   */
  const changeSearchConditionByItemTpChoCd = async (itemTpChoCd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpIprvPurpChg((prev) => ({
        ...prev,
        itemStatCd: '', // Item 상태
      }));
    }

    let mpItemStatus = [] as Code[];
    // MP 유형에 따른 검색조건 코드 조회
    if (itemTpChoCd) {
      // 1. Item 상태
      mpItemStatus = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_ITEM_STAT_CD',
        optValCtn1: itemTpChoCd,
      });
    }
    setCode((prev) => ({
      ...prev,
      mpItemStatus: mpItemStatus,
    }));
  };

  const ecoYnCdNm = useMemo(() => {
    const cd = ecoYNcodes.filter((o) => o.cmnCd === mpIprvPurpChg?.ecoYn);
    return cd.length ? cd[0].cmnCdNm : '';
  }, [mpIprvPurpChg?.ecoYn]);

  const handleItemChange = (name: string, value: string | string[]) => {
    setMpIprvPurpChg((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  return (
    <>
      <CustomDialog
        title={title}
        open={open}
        onClose={close}
        onCancel={close}
        size={'md'}
        buttons={dialogButtons}
      >
        <TableContainer css={tb.table} style={{ marginBottom: '200px' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <span className="dot">{String(t('mp.label.개선목적', '개선목적'))}</span>
                </TableCell>
                <TableCell className="duplication" colSpan={3}>
                  <div>
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={code?.mpImprTypeLvl1}
                      defaultValue={''}
                      onChange={(value) => {
                        handleItemChange('iprvClsfLv1Cd', value);
                        changeSearchConditionByIprvClsfLv1Cd(value);
                      }}
                      // readOnly={false}
                      isError={errors?.iprvClsfLv1Cd}
                      msgError={String(
                        t('mp.msg.개선목적 LV1을 선택해 주세요.', '개선목적 LV1을 선택해 주세요.')
                      )}
                    />
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={code?.mpImprTypeLvl2}
                      defaultValue={''}
                      onChange={(value) => {
                        handleItemChange('iprvClsfLv2Cd', value);
                        changeSearchConditionByIprvClsfLv2Cd(value);
                      }}
                      readOnly={false}
                      isError={errors?.iprvClsfLv2Cd}
                      msgError={String(
                        t('mp.msg.개선목적 LV2을 선택해 주세요.', '개선목적 LV2을 선택해 주세요.')
                      )}
                    />
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={code?.mpImprTypeLvl3}
                      defaultValue={''}
                      onChange={(value) => {
                        handleItemChange('iprvClsfLv3Cd', value);
                        const cd = (code?.mpImprTypeLvl3 || []).filter((o) => o.cmnCd === value);
                        if (cd.length) {
                          const code = ecoYNcodes.filter(
                            (o) => o.cmnCdNm === cd[0]?.optValCtn3 || ''
                          );
                          console.log(code);
                          setMpIprvPurpChg((prev) => ({
                            ...prev,
                            ecoYn: code.length > 0 ? code[0].cmnCd : '',
                          }));
                        }
                      }}
                      readOnly={false}
                      isError={errors?.iprvClsfLv3Cd}
                      msgError={String(
                        t('mp.msg.개선목적 LV3을 선택해 주세요.', '개선목적 LV3을 선택해 주세요.')
                      )}
                    />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="dot">{t('com.label.변경사유', '변경사유')}</span>
                </TableCell>
                <TableCell>
                  <CustomInputWithSearch
                    type="text"
                    name="rmk"
                    placeholder={String(
                      t('com.label.변경 사유입력를 입력하세요.', '변경 사유입력를 입력하세요.')
                    )}
                    value={mpIprvPurpChg?.rmk}
                    onChange={(e) => {
                      setMpIprvPurpChg({
                        ...mpIprvPurpChg,
                        rmk: e.target.value,
                      });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <span>{String(t('mp.label.NC/EC', 'NC/EC'))}</span>
                </TableCell>
                <TableCell>{mpIprvPurpChg?.ecoYn && (ecoYnCdNm || mpIprvPurpChg?.ecoYn)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CustomDialog>
    </>
  );
};
